/**
 * Page state in the URL (ported from d2d-DLP; ZTA design evaluation F24, Phase 3).
 *
 * Filters, tabs, paging and the open record were `useState`, so a refresh
 * or Back threw them away and a link could only ever open a page's default
 * view. Each page now names its params and their defaults once, and reads
 * and writes them through this hook:
 *
 *   const [url, setUrl] = useUrlState({ tab: 'events', q: '', page: '1' });
 *   setUrl({ tab: 'offenders' });                    // a new history entry
 *   setUrl({ q: next, page: null }, { replace: true }); // typing: no entry per key
 *
 * A param at its default is left out of the URL, so a page's plain URL is
 * its default view. `null` resets a param to its default.
 *
 * History: a discrete step (a tab, a select, a page, opening a record) pushes
 * an entry, so Back undoes it. Typing replaces the current entry, so Back
 * returns to the previous view rather than walking back one keystroke at a
 * time.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TimeRange } from './timeRange';
import { readTimeRange, writeTimeRange } from './urlTimeRange';

export type UrlPatch<K extends string> = Partial<Record<K, string | number | null | undefined>>;

export interface UrlWriteOptions {
  /** Replace the current history entry instead of pushing one (for typing). */
  replace?: boolean;
  /** Also write a time range (lib/urlTimeRange.ts form). */
  time?: TimeRange;
}

export function useUrlState<D extends Record<string, string>>(defaults: D) {
  const [params, setParams] = useSearchParams();

  // `defaults` is usually an inline literal; key the memo on its content.
  const defaultsKey = JSON.stringify(defaults);
  const values = useMemo(() => {
    const out = {} as Record<keyof D, string>;
    for (const key of Object.keys(defaults) as (keyof D & string)[]) {
      out[key] = params.get(key) ?? defaults[key];
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, defaultsKey]);

  const update = useCallback(
    (patch: UrlPatch<keyof D & string>, opts: UrlWriteOptions = {}) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, raw] of Object.entries(patch) as [keyof D & string, string | number | null | undefined][]) {
            const value = raw === null || raw === undefined ? defaults[key] : String(raw);
            if (value === defaults[key] || value === '') next.delete(key);
            else next.set(key, value);
          }
          if (opts.time) writeTimeRange(next, opts.time);
          return next;
        },
        { replace: opts.replace },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setParams, defaultsKey],
  );

  return [values, update, params] as const;
}

/**
 * The page's time range from the URL (`range`, or `from` / `to`), and a
 * setter that writes it back as a new history entry. `extraReset` names
 * params to clear alongside it, e.g. `page`, since a new window starts the
 * results over.
 */
export function useUrlTimeRange(fallback: TimeRange, extraReset: readonly string[] = []) {
  const [params, setParams] = useSearchParams();
  const fallbackKey = JSON.stringify(fallback);
  const range = useMemo(
    () => readTimeRange(params, fallback),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params, fallbackKey],
  );
  const resetKey = extraReset.join(',');
  const setRange = useCallback(
    (next: TimeRange) => {
      setParams((prev) => {
        const p = new URLSearchParams(prev);
        for (const key of extraReset) p.delete(key);
        // The default range is the plain URL, like any other default.
        if (JSON.stringify(next) === fallbackKey) return writeTimeRange(p, { kind: 'all' });
        return writeTimeRange(p, next);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setParams, fallbackKey, resetKey],
  );
  return [range, setRange] as const;
}
